import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Loader2, Trash2 } from 'lucide-react';
import ListingCard from '../components/ListingCard';
import { useFavorites } from '../context/FavoritesContext';
import { listingsApi } from '../api/client';

const FavoritesPage = () => {
  const { favorites, clear } = useFavorites();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (favorites.length === 0) { setListings([]); setLoading(false); return; }
    setLoading(true);
    // Fetch all listings and filter — simple approach for MVP
    listingsApi.list({ page: 1, page_size: 100 })
      .then((res) => {
        // Also fetch pages 2-5 if needed
        return Promise.all(
          Array.from({ length: Math.min(5, res.total_pages) - 1 }, (_, i) =>
            listingsApi.list({ page: i + 2, page_size: 100 })
          )
        ).then((rest) => {
          const all = [res, ...rest].flatMap((r) => r.items);
          const idSet = new Set(favorites);
          const found = all.filter((l) => idSet.has(l.id));
          // Preserve order of favorites
          const byId = Object.fromEntries(found.map((l) => [l.id, l]));
          setListings(favorites.map((id) => byId[id]).filter(Boolean));
        });
      })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [favorites]);

  return (
    <div className="min-h-screen bg-[#0f0a0c]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#a83a5b] to-[#5e1c30] flex items-center justify-center">
              <Heart className="w-6 h-6 text-white fill-white" />
            </div>
            <div>
              <h1 className="font-serif text-3xl text-white leading-tight">Meine Favoriten</h1>
              <p className="text-sm text-[#9a8a83]">{favorites.length} gespeicherte Inserate</p>
            </div>
          </div>
          {favorites.length > 0 && (
            <button onClick={() => { if (window.confirm('Alle Favoriten entfernen?')) clear(); }}
              className="px-4 py-2.5 rounded-md border border-[#2a1f24] text-[#ff8aa3] hover:text-white hover:bg-[#5c1a1a] hover:border-[#5c1a1a] inline-flex items-center gap-2 text-sm">
              <Trash2 className="w-4 h-4" /> Alle entfernen
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-[#9a8a83]"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#2a1f24] rounded-xl">
            <Heart className="w-10 h-10 mx-auto mb-3 text-[#5a4d48]" />
            <p className="text-[#9a8a83] mb-4">Du hast noch keine Inserate als Favorit gespeichert.</p>
            <Link to="/inserate"
              className="px-5 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] text-white font-medium inline-flex items-center gap-2">
              Inserate durchstöbern
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
